import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAppForm } from "@/components/form/form-context";
import { predefinedTextsQueryOptions, updatePredefinedTextsFn } from "@/server/organisations";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ResponsiveActionBar, ResponsiveFormSection } from "@/components/form/responsive-form";
import { useEffect, useState } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/predefined-texts")({
  component: RouteComponent,
  loader: async ({ params }) => {
    return {
      crumb: "Predefined texts",
      orgId: params.orgId,
    };
  },
});

function RouteComponent() {
  const { orgId } = Route.useLoaderData();
  const predefinedTextsQuery = useQuery(predefinedTextsQueryOptions(Number(orgId)));
  const predefinedTexts = predefinedTextsQuery.data;
  const queryClient = useQueryClient();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    void predefinedTextsQuery.refetch();
  }, [orgId, predefinedTextsQuery.refetch]);

  const updatePredefinedTextsMutation = useMutation({
    mutationFn: updatePredefinedTextsFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["predefinedTexts"] });
    },
    onError: (error) => {
      console.error("Error updating predefined texts:", error);
    },
  });

  const updatePredefinedTexts = async (values: any) => {
    try {
      const updatePredefinedTextsPromise = updatePredefinedTextsMutation.mutateAsync({
        data: { orgId: Number(orgId), payload: values },
      });
      toast.promise(updatePredefinedTextsPromise, {
        loading: "Updating predefined texts...",
        success: (data) => {
          queryClient.invalidateQueries({ queryKey: ["predefinedTexts"] });
          form.reset(data.predefinedTexts.data);
          return "Predefined texts updated";
        },
        error: (error) => `Failed to update predefined texts: ${error?.message}`,
      });
    } catch (error) {
      console.error("Error updating predefined texts:", error);
    }
  };

  const initiateFormValues = () => {
    return (
      predefinedTexts || {
        shiftF1: null,
        shiftF2: null,
        shiftF3: null,
        shiftF4: null,
        shiftF5: null,
        shiftF6: null,
        shiftF7: null,
        shiftF8: null,
        shiftF9: null,
        shiftF10: null,
        shiftF11: null,
        shiftF12: null,
      }
    );
  };

  const form = useAppForm({
    defaultValues: initiateFormValues(),
    onSubmit: async ({ value }) => {
      await updatePredefinedTexts(value);
    },
  });

  const getFieldLabel = (key: string) => {
    return key.split("F").join(" + F");
  };

  return (
    <ResponsiveFormSection>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="mb-4">Predefined texts</CardTitle>
            <div className="text-sm">
              Keyboard shortcuts for text that are frequently written. Use $1 for Agent First name, $2 for Agent Last
              name and $3 for Agent Sip Presentation Address.
            </div>
          </CardHeader>
          <CardContent>
            <section className="flex flex-col gap-8">
              {Object.keys(predefinedTexts || form.state.values).map((key) => (
                <form.AppField
                  key={key}
                  name={
                    key as
                      | "shiftF1"
                      | "shiftF2"
                      | "shiftF3"
                      | "shiftF4"
                      | "shiftF5"
                      | "shiftF6"
                      | "shiftF7"
                      | "shiftF8"
                      | "shiftF9"
                      | "shiftF10"
                      | "shiftF11"
                      | "shiftF12"
                  }
                  children={(field) => <field.FormTextField label={getFieldLabel(key)} id={key} />}
                />
              ))}
            </section>
          </CardContent>
        </Card>
        <form.Subscribe selector={(state) => [state.isSubmitting, state.isDirty, state.canSubmit]}>
          {([isSubmitting, isDirty, canSubmit]) => (
            <ResponsiveActionBar>
              <div className="flex gap-2">
                <Button type="submit" disabled={isSubmitting || !isDirty || !canSubmit}>
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting || !isDirty || !canSubmit}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCancelDialogOpen(true);
                  }}
                >
                  Reset
                </Button>
              </div>
            </ResponsiveActionBar>
          )}
        </form.Subscribe>
      </form>
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Discard changes"
        description="Are you sure you want to undo all unsaved changes? This will reset the form back to its initial state."
        confirmLabel="Reset"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onConfirm={() => {
          form.reset(initiateFormValues());
          setCancelDialogOpen(false);
        }}
        onCancel={() => {
          setCancelDialogOpen(false);
        }}
      />
    </ResponsiveFormSection>
  );
}
